import React, { useEffect, useMemo, useRef, useState } from 'react';
import Overlay from '../../components/Overlay';
import ActionButton from '../../components/ActionButton';
import QtyStepper from '../../components/QtyStepper';
import { setWeldReserve, setWeldReserves } from '../../lib/welding';
import { useTier, controlHeight } from '../../lib/tiers';
import {
  RESERVE_MAX, reserveOf, typedPercent, percentOf, applyPercent, stillToWeldWith,
  sizesByName, startRows, reserveChanges, rowChanged,
} from '../../lib/weldingReserves';
import {
  refusalWords, SAVING, TYPE_FIRST, NOTHING_CHANGED,
  reserveTitle, RESERVE_HELP, reserveWasLine, reservePreview, setReserveLabel, reserveSetAnswer,
  reservesTitle, RESERVES_HELP, FILL_LABEL, APPLY_LABEL, ROUNDED_NOTE, TYPE_PERCENT,
  reserveWasShort, saveChangesLabel, reservesSavedAnswer, CANCEL_LABEL,
} from '../../lib/weldingWords';

// #746 - CHANGING A RESERVE (gate 746, section 4.5, R1 to R4; 746 D29-D31),
// Session 79.22b.6.4b. BUILT EXACTLY AS GROK DREW IT (746 D25;
// docs/gates/drawings/2026-10-10_welding_746_reserves_grok.html), every word
// the copy table's (weldingWords.js). Shown only to someone canSetWeldReserve()
// lets in - a manager at the welding branch, an admin or an executive - and the
// database's weld_reserve_ok() refuses anyone else whatever a screen shows.
//
//   R2 - one size: Change on the size's sheet turns the sheet into this, in
//        place, as Recount welded frames does (F3). On the desk it is the
//        size's window, in R4's style (undrawn there; section 4's box).
//   R3 - every size at once, on a phone and a tablet: a sheet at the app's 90%
//        cap, its list scrolling inside and its footer fixed.
//   R4 - the same on the desk, as a dialog over F6.
//
// A change is never undone (the answer bar carries no Undo): each one is kept
// with who, when, the old number and the new (746 D30), and changing it back is
// a change of its own.
//
// "CHANGED MEANWHILE" (F3): each save sends the number the screen OPENED ON. If
// somebody changed a reserve since, the database writes nothing at all and says
// which size and its current number; the page re-reads, and the box (R2) or every
// box (R3, R4) takes the current number.

// The drawn number box's line height is its own height less its border
// (`.qty`: line-height 46px in a 48px box, 30 in the desk's 32) - the shared
// stepper's height is the pointer's (QtyStepper.js), so this follows it.
function useBoxLine() {
  const { coarse } = useTier();
  const h = coarse ? controlHeight(true, 'commit') : controlHeight(false, 'commit');
  return `${h - 2}px`;
}

// R2 - ONE SIZE'S RESERVE. Module level (locked rule 2).
export function WeldingReserveBody({ size, branchId, desk, onClose, onSaved, onChanged }) {
  const boxLine = useBoxLine();
  const [base, setBase] = useState(() => Math.max(Number(size.reserve) || 0, 0));
  const [qty, setQty] = useState(() => String(Math.max(Number(size.reserve) || 0, 0)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [stale, setStale] = useState(false);
  // A press with the box empty re-announces the preview's "Type how many
  // first." rather than printing it twice (#492: a press always answers).
  const [nudge, setNudge] = useState(0);
  const titleRef = useRef(null);
  useEffect(() => { titleRef.current?.focus(); }, []);

  // Changed meanwhile: once the page has re-read, the box takes the current number.
  const current = Math.max(Number(size.reserve) || 0, 0);
  useEffect(() => {
    if (!stale || current === base) return;
    setBase(current);
    setQty(String(current));
    setStale(false);
  }, [stale, current, base]);

  const n = reserveOf(qty);
  const previewId = `wp-rpreview-${size.itemId}`;

  async function save() {
    setError('');
    if (n == null) { setNudge(k => k + 1); return; }
    if (n === base) { setError(NOTHING_CHANGED); return; }
    setSaving(true);
    const r = await setWeldReserve({ branchId, itemId: size.itemId, reserve: n, was: base });
    setSaving(false);
    if (!r.ok) {
      // The sheet stays open with its reason under the button (F3).
      setError(refusalWords(r.error));
      if (String(r.error?.code || '') === '55000') setStale(true);
      onChanged?.();
      return;
    }
    onSaved?.({ text: r.changed ? reserveSetAnswer(size.name, r.reserve ?? n) : NOTHING_CHANGED });
  }

  return (
    <>
      <div className="wp-shtop">
        <h3 ref={titleRef} tabIndex={-1}>{reserveTitle(size.name)}</h3>
        <button type="button" className="wp-x" aria-label="Close" onClick={onClose}>×</button>
      </div>
      <p className="wp-ssub">{RESERVE_HELP}</p>
      <p className="wp-rwas">{reserveWasLine(base)}</p>
      <QtyStepper
        className="wp-rstep"
        size="commit"
        label="Reserve"
        min={0}
        max={RESERVE_MAX}
        boxStyle={{ lineHeight: boxLine }}
        value={qty}
        onChange={v => { setQty(v); setError(''); }}
      />
      <p key={nudge} className="wp-preview" id={previewId} role={nudge ? 'alert' : undefined}>
        {n == null ? TYPE_FIRST : reservePreview(n, stillToWeldWith(size, n))}
      </p>
      <ActionButton
        className="wp-btn pri"
        busy={saving}
        busyLabel={SAVING}
        onClick={save}
        {...(n == null ? { 'aria-disabled': true, 'aria-describedby': previewId } : {})}
      >
        {setReserveLabel(qty)}
      </ActionButton>
      {error && <p className="wp-moved" role="alert">{error}</p>}
    </>
  );
}

// THE PERCENT BOX (F3): typed, -100 to 1,000; 40 to a finger and 32 to a mouse,
// its type at the drawn 26 (never below 16px, locked rule 12 - set inline,
// because the app's global input rule outranks a class, #166).
function PercentBox({ value, onChange }) {
  const { coarse } = useTier();
  const h = controlHeight(coarse, 'min') < 32 ? 32 : controlHeight(coarse, 'min');
  return (
    <input
      className="wp-rpct"
      type="text"
      inputMode="numeric"
      autoComplete="off"
      enterKeyHint="done"
      aria-label="Percent"
      value={value}
      onChange={e => onChange(typedPercent(value, e.target.value))}
      onFocus={e => e.target.select()}
      style={{ height: h, fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: 26, letterSpacing: '.5px', lineHeight: `${h - 2}px` }}
    />
  );
}

// R3 AND R4 - EVERY SIZE AT ONCE. `sizes` is every size the board read, all
// three states (weldingDemand()'s note): a size with nothing on order can still
// have a reserve to set. Module level (locked rule 2).
export function WeldingReservesBody({ sizes, branchId, branchName, desk, onClose, onSaved, onChanged }) {
  const boxLine = useBoxLine();
  const ordered = useMemo(() => sizesByName(sizes), [sizes]);
  const [rows, setRows] = useState(() => startRows(ordered));
  const [pct, setPct] = useState('');
  const [fillError, setFillError] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [stale, setStale] = useState(false);
  const titleRef = useRef(null);
  useEffect(() => { titleRef.current?.focus(); }, []);

  // Changed meanwhile: every box returns to its current number once the page
  // has re-read (F3) - that is, once some size's current reserve is no longer
  // the number its box started from, which may already be true when the
  // refusal arrives, if the change came in live first.
  useEffect(() => {
    if (!stale) return;
    const moved = ordered.some(s => {
      const r = rows[String(s.itemId)];
      return r && r.start !== Math.max(Number(s.reserve) || 0, 0);
    });
    if (!moved) return;
    setRows(startRows(ordered));
    setStale(false);
  }, [stale, ordered, rows]);

  // A size that arrived while the list was open starts at its current number.
  const rowOf = s => rows[String(s.itemId)] || { start: Math.max(Number(s.reserve) || 0, 0), value: String(Math.max(Number(s.reserve) || 0, 0)) };
  const all = Object.fromEntries(ordered.map(s => [String(s.itemId), rowOf(s)]));
  const { changes, incomplete } = reserveChanges(ordered, all);

  function setValue(s, v) {
    setRows(prev => ({ ...prev, [String(s.itemId)]: { ...rowOf(s), value: v } }));
    setError('');
  }

  function apply() {
    setFillError('');
    const p = percentOf(pct);
    if (p == null) { setFillError(TYPE_PERCENT); return; }
    setRows(Object.fromEntries(ordered.map(s => {
      const r = rowOf(s);
      return [String(s.itemId), { ...r, value: String(applyPercent(r.start, p)) }];
    })));
    setError('');
  }

  async function save() {
    setError('');
    if (incomplete) { setError(TYPE_FIRST); return; }
    if (!changes.length) { setError(NOTHING_CHANGED); return; }
    setSaving(true);
    const r = await setWeldReserves({ branchId, changes });
    setSaving(false);
    if (!r.ok) {
      setError(refusalWords(r.error));
      if (String(r.error?.code || '') === '55000') setStale(true);
      onChanged?.();
      return;
    }
    onSaved?.({ text: r.changed > 0 ? reservesSavedAnswer(r.changed) : NOTHING_CHANGED });
  }

  const row = s => {
    const r = rowOf(s);
    const name = (
      <div className="wp-rnm">
        <div className="wp-rname">{s.name}</div>
        {rowChanged(r) && <div className="wp-rwasshort">{reserveWasShort(r.start)}</div>}
      </div>
    );
    return (
      <div key={s.itemId} className={desk ? 'wp-rtr' : 'wp-rrow'}>
        {name}
        <QtyStepper
          className="wp-rmini"
          size="commit"
          label={s.name}
          fewerLabel={`One fewer ${s.name}`}
          moreLabel={`One more ${s.name}`}
          min={0}
          max={RESERVE_MAX}
          value={r.value}
          boxStyle={{ flex: 'none', width: 64, lineHeight: boxLine }}
          onChange={v => setValue(s, v)}
        />
      </div>
    );
  };

  return (
    <>
      <div className="wp-shtop">
        <h3 ref={titleRef} tabIndex={-1}>{reservesTitle(branchName)}</h3>
        <button type="button" className="wp-x" aria-label="Close" onClick={onClose}>×</button>
      </div>
      <p className="wp-ssub">{RESERVES_HELP}</p>
      <div className="wp-rfill">
        <p>{FILL_LABEL}</p>
        <PercentBox value={pct} onChange={v => { setPct(v); setFillError(''); }} />
        <p>%</p>
        <button type="button" className="wp-btn wp-btn40" onClick={apply}>{APPLY_LABEL}</button>
      </div>
      <p className="wp-rnote">{ROUNDED_NOTE}</p>
      {fillError && <p className="wp-moved" role="alert">{fillError}</p>}
      <div className="wp-rlist">
        {desk && <div className="wp-rtr head"><span>Size</span><span>Reserve</span></div>}
        {ordered.map(row)}
      </div>
      <div className="wp-rfoot">
        <ActionButton className="wp-btn pri" busy={saving} busyLabel={SAVING} onClick={save}>
          {saveChangesLabel(changes.length)}
        </ActionButton>
        <button type="button" className="wp-btn" onClick={onClose}>{CANCEL_LABEL}</button>
      </div>
      {error && <p className="wp-moved" role="alert">{error}</p>}
    </>
  );
}

// R3 - THE PHONE AND TABLET SHEET: Overlay's bottom sheet held at the app's 90%
// cap, the list scrolling inside and the footer fixed (F3).
export function WeldingReservesSheet(props) {
  return (
    <Overlay
      variant="sheet"
      onClose={props.onClose}
      panelStyle={{
        borderTop: 'none', borderLeft: 'none', borderRight: 'none', boxShadow: 'var(--shadow-md)',
        height: '90vh', display: 'flex', flexDirection: 'column', overflowY: 'hidden',
      }}
    >
      <div className="wp-sheet wp-rall">
        <div className="wp-grab" aria-hidden="true" />
        <WeldingReservesBody {...props} />
      </div>
    </Overlay>
  );
}

// R4 - THE DESK'S DIALOG: 720 wide, 72px under the top of the window as drawn,
// rounded 12, a mouse's sizes; it scrolls inside if the window is short.
export function WeldingReservesDialog(props) {
  return (
    <Overlay
      variant="modal"
      onClose={props.onClose}
      maxWidth={720}
      panelStyle={{
        border: 'none', borderRadius: 12, boxShadow: 'var(--shadow-md)',
        alignSelf: 'flex-start', marginTop: 72, maxHeight: 'calc(100vh - 88px)',
      }}
    >
      <div className="wp-sheet desk wp-rall wp-rdlg">
        <WeldingReservesBody {...props} desk />
      </div>
    </Overlay>
  );
}
